/**
 * Typesense filter API
 *
 * Drop-in replacement for the Storefront-backed `filter` module (`./filter.js`). It answers
 * `fetch()` and `nextPage()` with exactly the response shape `filter.js` returns, so
 * `<collection-filters>`, the product cards, the URL parameters (`filter.p.m.custom.*`,
 * `filter.v.price`) and the sort options work unchanged.
 *
 * Enabled from the theme settings ("Collection filtering"), which expose
 * `window.theme.typesense = { host, key }` and load this file as a module
 * (`snippets/head-scripts.liquid`) before `bundle.js`. It registers itself as
 * `window.theme.filterAdapter`; the bundle then calls `init()` right after the Storefront
 * `filter` module, before `<collection-filters>` asks for products. Other modules import it as
 * `@theme/typesense`. When it is off the file is not loaded and the Storefront module stays
 * in charge.
 *
 * The index holds one document per product with its metafield facets flattened into fields
 * of the same name (`custom.metal` -> `metal`). Results are grouped by `metal_group`, so a
 * design comes back once however many metal variants it has.
 *
 * @module typesense
 */

const COLLECTION = 'products';

/**
 * Filters in Search & Discovery display order.
 *
 * `key` is the `custom.<key>` metafield the Storefront filter id is built from, and also the
 * Typesense field name.
 */
export const FACETS = [
  { key: 'center_stone_shape', label: 'Shape' },
  { key: 'stone', label: 'Stone' },
  { key: 'origin', label: 'Origin' },
  { key: 'metal', label: 'Metal' },
  { key: 'zodiac_sign', label: 'Zodiac Sign' },
  { key: 'gender', label: 'Gender' },
  { key: 'style', label: 'Style' },
  { key: 'product_type', label: 'Product Type' },
  { key: 'material', label: 'Material' },
  { key: 'stone_shape', label: 'Shape' },
  { key: 'stone_type', label: 'Gemstone' },
  { key: 'stone_carat_weight', label: 'Carat Weight', numeric: true }
];

export const PRICE_FILTER_ID = 'filter.v.price';

const PRICE_FIELD = 'price_min';

/**
 * The index holds prices in the store's currency (USD). Shopify Markets converts them for
 * other countries — every market here converts from USD, none has its own price list — so
 * prices the shopper sees and types are `Shopify.currency.rate` times the indexed ones.
 *
 * @returns {number} Shop currency -> active currency
 */
export const currencyRate = () => {

  const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);

  return isNaN(rate) || rate <= 0 ? 1 : rate;
};

const INCLUDE_FIELDS = 'handle,tags,metal_group,stone_type,seo_hidden';

/**
 * Fields a search query is matched against, most important first: Typesense scores a match in an
 * earlier field higher, so a word in the title beats the same word in the description. Shared by
 * the results page and the header search panel so both rank the same way.
 */
export const SEARCH_FIELDS = 'title,tags,vendor,stone,metal,style,description';

/**
 * Search order: best match first; between equal matches, the better-selling design (sales of all its
 * metals), then `custom.rank`.
 */
export const SEARCH_SORT = '_text_match:desc,group_units_sold:desc,rank:asc';

// Metal and origin follow the merchant's manual order in Search & Discovery, not the alphabet.
const METAL_COLOR_ORDER = ['rose', 'white', 'yellow', 'two-tone'];
const ORIGIN_ORDER = ['natural', 'lab grown'];

/**
 * Handle a facet value the way Shopify builds filter value ids ("Men's" -> "men-s",
 * "0.25" -> "0-25").
 *
 * @param {string} value
 * @returns {string}
 */
export const handleizeValue = (value) => String(value)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/**
 * Metal values were normalised to a lower-case karat ("14k White Gold"), while old links may
 * still carry "14K White Gold". Typesense filters match case-insensitively, but normalising
 * keeps labels consistent.
 *
 * @param {string} value
 * @returns {string}
 */
export const normalizeMetal = (value) => String(value).replace(/\b(\d+)K\b/g, '$1k');

/**
 * Quote a value for `filter_by`.
 *
 * @param {string} value
 * @returns {string}
 */
const quote = (value) => `\`${String(value).replace(/`/g, '')}\``;

/**
 * Group the Storefront filter inputs by field.
 *
 * @param {Array} filters - Storefront `ProductFilter` inputs (`{productMetafield}` / `{price}`)
 * @returns {{ values: Object<string, string[]>, price: ?{min: number, max: number} }}
 */
export const groupFilters = (filters = []) => {

  const grouped = { values: {}, price: null };

  (filters || []).forEach(filter => {

    if (!filter) return;

    if (filter.price) {
      grouped.price = filter.price;
      return;
    }

    const metafield = filter.productMetafield;

    if (!metafield || metafield.namespace !== 'custom') return;

    // An unknown field would make Typesense reject the whole request.
    if (!FACETS.some(facet => facet.key === metafield.key)) {
      console.warn(`Typesense: no facet for custom.${metafield.key}, filter ignored`);
      return;
    }

    const value = metafield.key === 'metal' ? normalizeMetal(metafield.value) : String(metafield.value);

    grouped.values[metafield.key] = grouped.values[metafield.key] || [];

    if (!grouped.values[metafield.key].includes(value)) {
      grouped.values[metafield.key].push(value);
    }
  });

  return grouped;
};

/**
 * `filter_by` clause for one facet: values within a facet are OR-ed.
 *
 * @param {string} key - Facet field
 * @param {string[]} values
 * @returns {string}
 */
export const facetClause = (key, values) => {

  const facet = FACETS.find(f => f.key === key);

  // Carat weights are float32 in the index, so exact equality is fragile; match a tight range.
  if (facet && facet.numeric) {
    const ranges = values
      .map(value => parseFloat(value))
      .filter(value => !isNaN(value))
      .map(value => `${(value - 0.0005).toFixed(4)}..${(value + 0.0005).toFixed(4)}`);

    return ranges.length ? `${key}:[${ranges.join(',')}]` : '';
  }

  return `${key}:=[${values.map(quote).join(',')}]`;
};

/**
 * `filter_by` clause for the price range. A product matches when its price range overlaps the
 * requested one, which is how Shopify matches "any variant priced within".
 *
 * @param {{min: number, max: number}} price
 * @returns {string}
 */
export const priceClause = (price) => {

  const clauses = [];
  const rate = currencyRate();
  // What the shopper typed is in their currency; the index is in USD.
  const min = Math.round((parseFloat(price.min) / rate) * 100) / 100;
  const max = Math.round((parseFloat(price.max) / rate) * 100) / 100;

  if (!isNaN(max) && max < 999999999) clauses.push(`price_min:<=${max}`);
  if (!isNaN(min) && min > 0) clauses.push(`price_max:>=${min}`);

  return clauses.join(' && ');
};

/** Centre-stone shapes of the `build_<shape>_min_carat_weight` fields. */
const BUILD_SHAPES = ['round', 'princess', 'cushion', 'emerald', 'asscher', 'marquise', 'oval', 'radiant', 'pear', 'heart'];

/**
 * Design Your Own grids (`sections/main-collection.liquid`) ask for collections Typesense never
 * indexes: `dyo-<product type>` before a diamond is picked, and, once one is,
 * `dyo-<product type>-<shape>-carat-weight-min-<carat>`, which `assets/dyo-api.js` creates on
 * demand through the app. The same rules as those Shopify collections, as clauses:
 *
 * - `custom.is_build_product` = Yes and `custom.product_type` = the product type;
 * - with a diamond, `build_<shape>_min_carat_weight` < carat + 0.01, so only settings that take
 *   that diamond show.
 *
 * They are part of the base filter, never a facet, so a shopper can't remove the carat limit and
 * end up with a setting their diamond doesn't fit. Clearance and hidden products are excluded by
 * buildFilterBy() as everywhere else.
 *
 * @param {string} handle
 * @returns {?string[]} Clauses, or null when the handle is not a Design Your Own one
 */
export const dyoClauses = (handle) => {

  const match = new RegExp(`^dyo-(.+?)(?:-(${BUILD_SHAPES.join('|')})-carat-weight-min-(\\d+)-(\\d+))?$`).exec(handle || '');

  if (!match) {
    return null;
  }

  // Typesense matches string values case-insensitively: "engagement ring" finds "Engagement Ring".
  const clauses = ['is_build_product:=true', `product_type:=${quote(match[1].replace(/-/g, ' '))}`];

  if (match[2]) {
    // Same limit as the Shopify rule assets/dyo-api.js creates: min carat weight < carat + 0.01.
    const limit = (parseFloat(`${match[3]}.${match[4]}`) + 0.01).toFixed(2);

    clauses.push(`build_${match[2]}_min_carat_weight:<${limit}`);
  }

  return clauses;
};

/**
 * Build `filter_by`.
 *
 * @param {Object} options
 * @param {?string} options.handle - Collection handle (null on search)
 * @param {Object} options.grouped - Output of `groupFilters`
 * @param {?string} options.exclude - Facet key (or `price`) to leave out, for disjunctive counts
 * @returns {string}
 */
export const buildFilterBy = ({ handle = null, grouped = { values: {}, price: null }, exclude = null }) => {

  const clauses = [];

  if (handle === 'clearance') {
    clauses.push('clearance:=true');
  } else {
    if (handle) {
      clauses.push(...(dyoClauses(handle) || [`collections:=${quote(handle)}`]));
    }

    // Clearance products show only on the clearance collection: never in search or any other
    // collection they belong to.
    clauses.push('clearance:=false');
  }

  clauses.push('seo_hidden:=false');

  Object.keys(grouped.values).forEach(key => {
    if (key === exclude || !grouped.values[key].length) return;

    const clause = facetClause(key, grouped.values[key]);

    if (clause) clauses.push(clause);
  });

  if (grouped.price && exclude !== 'price') {
    const clause = priceClause(grouped.price);

    if (clause) clauses.push(clause);
  }

  return clauses.join(' && ');
};

/**
 * Build `sort_by` from the Storefront sort key.
 *
 * Title is not a sort field in the index, so "A to Z" falls back to the default order.
 *
 * @param {?string} sortKey - BEST_SELLING, MANUAL, PRICE, CREATED, TITLE, RELEVANCE
 * @param {boolean} reverse
 * @param {boolean} isSearch
 * @returns {string}
 */
export const buildSortBy = (sortKey, reverse = false, isSearch = false) => {

  const direction = (ascending) => (ascending !== !!reverse ? 'asc' : 'desc');
  const tiebreak = isSearch ? '_text_match:desc' : 'rank:asc';

  switch (sortKey) {
    case 'PRICE':
      return `price_min:${direction(true)},${tiebreak}`;
    case 'CREATED':
    case 'CREATED_AT':
      return `created_at:${direction(true)},${tiebreak}`;
    case 'BEST_SELLING':
      // Units sold over the last 365 days (set by the app's nightly reindex): designs by the sales of
      // all their metals, and within a design the best-selling metal first, so it is the one shown.
      return isSearch ? `group_units_sold:desc,units_sold:desc,${tiebreak}` : 'group_units_sold:desc,units_sold:desc,rank:asc';
    default:
      return isSearch ? SEARCH_SORT : 'rank:asc,created_at:desc';
  }
};

/**
 * Search parameters for one request.
 *
 * @param {Object} options
 * @returns {Object}
 */
export const buildSearchParams = ({ handle, terms, grouped, sortKey, reverse, page, perPage }) => {

  const isSearch = !handle;

  return {
    q: isSearch ? terms : '*',
    query_by: isSearch ? SEARCH_FIELDS : 'title',
    filter_by: buildFilterBy({ handle, grouped }),
    sort_by: buildSortBy(sortKey, reverse, isSearch),
    group_by: 'metal_group',
    group_limit: 1,
    per_page: perPage,
    page: page,
    facet_by: [...FACETS.map(facet => facet.key), PRICE_FIELD].join(','),
    max_facet_values: 250,
    include_fields: INCLUDE_FIELDS
  };
};

/**
 * Facet-only requests, one per active facet, run without that facet's own selection.
 *
 * Filtering by "14k White Gold" must not make "14k Rose Gold" disappear from the Metal list —
 * values within a facet are OR-ed, so each facet's values are counted as if it were unselected.
 *
 * @param {Object} params - Main request parameters
 * @param {string} handle
 * @param {Object} grouped
 * @returns {Array<{field: string, params: Object}>}
 */
export const buildDisjunctiveSearches = (params, handle, grouped) => {

  const searches = [];

  Object.keys(grouped.values).forEach(key => {
    if (!grouped.values[key].length) return;

    searches.push({ field: key, params: { ...params, filter_by: buildFilterBy({ handle, grouped, exclude: key }), facet_by: key, per_page: 0, page: 1 } });
  });

  if (grouped.price) {
    searches.push({ field: PRICE_FIELD, params: { ...params, filter_by: buildFilterBy({ handle, grouped, exclude: 'price' }), facet_by: PRICE_FIELD, per_page: 0, page: 1 } });
  }

  return searches;
};

/**
 * Order facet values the way Search & Discovery lists them.
 *
 * @param {string} key
 * @param {Array<{value: string}>} counts
 * @returns {Array}
 */
export const sortFacetValues = (key, counts) => {

  const byLabel = (a, b) => a.value.localeCompare(b.value, 'en', { sensitivity: 'base' });

  if (key === 'stone_carat_weight') {
    return [...counts].sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
  }

  if (key === 'origin') {
    const rank = (value) => {
      const index = ORIGIN_ORDER.indexOf(value.toLowerCase());
      return index === -1 ? ORIGIN_ORDER.length : index;
    };

    return [...counts].sort((a, b) => rank(a.value) - rank(b.value) || byLabel(a, b));
  }

  if (key === 'metal') {
    // "14k Rose Gold": colour first, then karat. Palladium, Platinum, Silver follow alphabetically.
    const parse = (value) => {
      const match = value.toLowerCase().match(/^(\d+)k\s+(.+?)\s+gold$/);

      if (!match) return { color: METAL_COLOR_ORDER.length, karat: 0 };

      const color = METAL_COLOR_ORDER.indexOf(match[2]);

      return { color: color === -1 ? METAL_COLOR_ORDER.length - 0.5 : color, karat: parseInt(match[1], 10) };
    };

    return [...counts].sort((a, b) => {
      const pa = parse(a.value);
      const pb = parse(b.value);

      return pa.color - pb.color || pa.karat - pb.karat || byLabel(a, b);
    });
  }

  return [...counts].sort(byLabel);
};

/**
 * Map Typesense facet counts to Storefront `filters`.
 *
 * Counts under `group_by` only reflect each group's top product, so they are not shown (the
 * list facet already keeps them hidden). They are still passed through because a value with
 * `count == 0` is skipped when rendering.
 *
 * @param {Array} facetCounts - `facet_counts` of the main request
 * @param {Object<string, Object>} overrides - Facet counts from the disjunctive requests, by field
 * @returns {Array}
 */
export const mapFilters = (facetCounts = [], overrides = {}) => {

  const byField = {};

  (facetCounts || []).forEach(facet => {
    byField[facet.field_name] = facet;
  });

  Object.keys(overrides).forEach(field => {
    byField[field] = overrides[field];
  });

  const filters = [];

  const price = byField[PRICE_FIELD];
  const maxPrice = price && price.stats && typeof price.stats.max === 'number' ? Math.ceil(price.stats.max * currencyRate()) : 0;

  filters.push({
    id: PRICE_FILTER_ID,
    label: 'Price',
    presentation: null,
    type: 'PRICE_RANGE',
    values: [{
      id: PRICE_FILTER_ID,
      label: 'Price',
      count: 0,
      input: JSON.stringify({ price: { min: 0, max: maxPrice } })
    }]
  });

  FACETS.forEach(facet => {

    const counts = (byField[facet.key] && byField[facet.key].counts || []).filter(item => item.count > 0 && item.value !== '');

    if (!counts.length) return;

    const id = `filter.p.m.custom.${facet.key}`;

    filters.push({
      id: id,
      label: facet.label,
      presentation: 'TEXT',
      type: 'LIST',
      values: sortFacetValues(facet.key, counts).map(item => ({
        id: `${id}.${handleizeValue(item.value)}`,
        label: item.value,
        count: item.count,
        input: JSON.stringify({ productMetafield: { namespace: 'custom', key: facet.key, value: item.value } })
      }))
    });
  });

  return filters;
};

/**
 * Map one Typesense document to the Storefront product node the grid consumes.
 *
 * @param {Object} document
 * @returns {{node: Object}}
 */
export const mapProduct = (document) => {

  const group = document.metal_group && !/^p\d+$/.test(document.metal_group) ? document.metal_group : null;
  const stoneType = Array.isArray(document.stone_type) ? document.stone_type.filter(Boolean) : (document.stone_type ? [document.stone_type] : []);

  return {
    node: {
      handle: document.handle,
      tags: document.tags || [],
      group: group ? { value: JSON.stringify([group]) } : null,
      stoneType: stoneType.length ? { value: stoneType.length === 1 ? stoneType[0] : JSON.stringify(stoneType) } : null,
      seoHidden: document.seo_hidden ? { value: 'true' } : null
    }
  };
};

/**
 * Map the Typesense result(s) to the `filter.js` response shape.
 *
 * @param {Object} result - Main search result
 * @param {Array<{field: string, result: Object}>} disjunctive - Facet-only results
 * @param {number} page
 * @param {number} perPage
 * @returns {Object}
 */
export const prepareResponse = (result, disjunctive = [], page = 1, perPage = 80) => {

  const hits = result.grouped_hits
    ? result.grouped_hits.map(group => group.hits && group.hits[0] && group.hits[0].document).filter(Boolean)
    : (result.hits || []).map(hit => hit.document);

  const overrides = {};

  disjunctive.forEach(({ field, result: facetResult }) => {
    const facet = (facetResult.facet_counts || []).find(f => f.field_name === field);

    if (facet) overrides[field] = facet;
  });

  const found = result.found || 0;
  const hasNextPage = page * perPage < found;

  return {
    products: hits.map(mapProduct),
    filters: mapFilters(result.facet_counts, overrides),
    pagination: {
      hasPreviousPage: page > 1,
      hasNextPage: hasNextPage,
      startCursor: String(page),
      endCursor: hasNextPage ? String(page + 1) : null
    },
    totalCount: found,
    // `found` counts designs across the whole result set, so it can be shown as-is.
    // The Storefront module only counts the batch it fetched and leaves this unset.
    exactCount: true
  };
};

const component = {

  /**
   * Take over `window.theme.filter` when Typesense is configured.
   *
   * Must run after `filter.init()`: the Storefront module is kept as the fallback.
   *
   * @returns {void}
   */
  init: () => {

    const config = window.theme.typesense;

    if (!config || !config.host || !config.key) {
      return;
    }

    component.config = config;
    component.storefront = window.theme.filter;

    window.theme.filter = component;

    window.theme.filter.state = {
      handle: null,
      variables: {},
      response: null,
      fallback: false
    };
  },

  /**
   * Fetch products from a collection or a search.
   *
   * Same signature as `filter.js#fetch`. `endCursor` is the page number to load.
   *
   * @param {string} handle - Collection handle
   * @param {string} terms - Search terms
   * @param {string} endCursor - Cursor for pagination
   * @param {Array} filters - Filters for products
   * @param {string} sortKey - Sort key for products
   * @param {boolean} reverse - Whether to reverse the sort
   * @param {number} limit - Number of designs per page. One design per group comes back, so 80
   *   fills a page on its own (the Storefront needs 240 products to de-duplicate down to 80).
   */
  fetch: async (handle = null, terms = null, endCursor = null, filters = [], sortKey = null, reverse = false, limit = 80) => {

    if (!handle && !terms) {
      return;
    }

    if (!handle && terms.trim() === '') {
      console.error('Search terms cannot be empty when performing a search query');
      return null;
    }

    const page = parseInt(endCursor, 10) || 1;
    const grouped = groupFilters(filters);
    const params = buildSearchParams({ handle, terms, grouped, sortKey, reverse, page, perPage: limit });
    const disjunctive = buildDisjunctiveSearches(params, handle, grouped);
    const started = performance.now();

    let response;
    let entry = null;

    try {
      const results = await component.search([params, ...disjunctive.map(search => search.params)]);

      entry = component.record({ handle, terms, page, params, result: results[0], ms: Math.round(performance.now() - started) });

      response = prepareResponse(results[0], disjunctive.map((search, index) => ({ field: search.field, result: results[index + 1] })), page, limit);

      // The Storefront `products` query behind the clearance page has no filters either.
      if (handle === 'clearance') {
        response.filters = [];
      }
    } catch (error) {
      console.error('Typesense request failed, falling back to the Storefront API', error);
      component.record({ handle, terms, page, params, error: String(error && error.message || error), fallback: true });
      return component.fallback(handle, terms, filters, sortKey, reverse);
    }

    // A collection created moments ago (the DYO compatibility collections) is not in the index
    // until the next sync. Only the Storefront knows about it yet.
    if (handle && page === 1 && response.totalCount === 0 && Object.keys(grouped.values).length === 0 && !grouped.price) {
      if (entry) entry.fallback = true;
      return component.fallback(handle, terms, filters, sortKey, reverse);
    }

    const state = window.theme.filter.state;

    state.handle = handle;
    state.variables = { terms, filters, sortKey, reverse, limit };
    state.response = response;
    state.fallback = false;

    return response;
  },

  /**
   * Fetch the next page of products
   *
   * @returns {Object} The response from the API
   */
  nextPage: async () => {

    const state = window.theme.filter.state;

    if (state.fallback) {
      return component.storefront.nextPage();
    }

    if (!state.response || !state.response.pagination.hasNextPage) {
      return null;
    }

    return component.fetch(
      state.handle,
      state.variables.terms,
      state.response.pagination.endCursor,
      state.variables.filters,
      state.variables.sortKey,
      state.variables.reverse,
      state.variables.limit
    );
  },

  /**
   * Grid requests and their raw results, kept only in debug mode (`?debug=true`) for the debug
   * drawer in `assets/theme-debug.js`.
   */
  log: [],

  /**
   * Keep a grid request in `log` when debug mode is on.
   *
   * @param {Object} entry - Request, its main result or error, and whether the Storefront took over
   * @returns {?Object} The kept entry, or null outside debug mode
   */
  record: (entry) => {

    if (!window.theme.debugMode) {
      return null;
    }

    const kept = { at: new Date().toISOString(), fallback: false, ...entry };

    component.log.push(kept);

    return kept;
  },

  /**
   * Hand the request to the Storefront module and keep paging through it.
   *
   * @returns {Object} The Storefront response
   */
  fallback: async (handle, terms, filters, sortKey, reverse) => {

    if (!component.storefront) {
      return null;
    }

    window.theme.filter.state.fallback = true;

    return component.storefront.fetch(handle, terms, null, filters, sortKey, reverse);
  },

  /**
   * Run one or more searches.
   *
   * A single search is a plain GET. Several go through `multi_search` in one POST. The key
   * travels as a query parameter and the body as text/plain so neither needs a CORS preflight.
   *
   * @param {Array<Object>} searches - Search parameters
   * @returns {Promise<Array<Object>>} One result per search
   */
  search: async (searches) => {

    const { host, key } = component.config;
    const base = `https://${host}`;

    if (searches.length === 1) {

      const query = new URLSearchParams({ ...searches[0], 'x-typesense-api-key': key });
      const response = await fetch(`${base}/collections/${COLLECTION}/documents/search?${query}`);

      if (!response.ok) {
        throw new Error(`Typesense search failed (${response.status})`);
      }

      return [await response.json()];
    }

    const response = await fetch(`${base}/multi_search?${new URLSearchParams({ 'x-typesense-api-key': key })}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ searches: searches.map(search => ({ collection: COLLECTION, ...search })) })
    });

    if (!response.ok) {
      throw new Error(`Typesense multi_search failed (${response.status})`);
    }

    const data = await response.json();

    // multi_search answers 200 even when a single search fails.
    data.results.forEach(result => {
      if (result.error || result.code) {
        throw new Error(`Typesense search failed: ${result.error || result.code}`);
      }
    });

    return data.results;
  }
};

export default component;

window.theme = window.theme || {};
window.theme.filterAdapter = component;
